import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Eye, EyeOff, Loader2, Lock, AlertCircle, Check, ShieldCheck,
} from "lucide-react";
import { usePortalAuth, portalApi } from "../../context/PortalAuthContext";
import PortalShell from "./PortalShell";
import SEO from "../../components/SEO";
import { TotpStep } from "../admin/Login";
import { formatApiError } from "../../lib/api";

function Rule({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      <span
        className={`w-4 h-4 flex items-center justify-center transition-colors ${
          ok ? "bg-[var(--te-primary)]" : "bg-white/10"
        }`}
      >
        {ok && <Check size={10} color="#fff" strokeWidth={3} />}
      </span>
      <span className={ok ? "text-zinc-200" : "text-zinc-500"}>{label}</span>
    </li>
  );
}

export default function PortalActivate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { customer, activate, verifyTotp } = usePortalAuth();

  const [state, setState] = useState("loading"); // loading | ready | totp | done | invalid
  const [emailHint, setEmailHint] = useState("");
  const [nameHint, setNameHint] = useState("");
  const [tokenError, setTokenError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const codeRef = useRef(null);
  useEffect(() => { if (state === "totp" && codeRef.current) codeRef.current.focus(); }, [state]);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.get(`/portal/auth/activate/${token}`);
        setEmailHint(r.data.email);
        setNameHint(r.data.name || "");
        setState("ready");
      } catch (err) {
        setTokenError(err?.response?.data?.detail || "This activation link is invalid or has expired.");
        setState("invalid");
      }
    })();
  }, [token]);

  if (customer && state !== "done") {
    return <Navigate to="/portal" replace />;
  }

  const rules = {
    length: password.length >= 10,
    digit: /\d/.test(password),
    symbol: /[^a-zA-Z0-9]/.test(password),
    match: password.length > 0 && password === confirm,
  };
  const allOk = rules.length && rules.digit && rules.symbol && rules.match;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!allOk) return;
    setSubmitting(true);
    try {
      const data = await activate(token, password);
      // Activation now returns a TOTP setup challenge instead of a JWT.
      setChallenge(data);
      setState("totp");
    } catch (err) {
      setSubmitError(formatApiError(err?.response?.data?.detail) || "Activation failed. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitting(true);
    try {
      await verifyTotp(challenge.challenge_token, code);
      setState("done");
      setTimeout(() => navigate("/portal", { replace: true }), 1200);
    } catch (err) {
      setSubmitError(formatApiError(err?.response?.data?.detail) || "Invalid code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalShell showNav={false}>
      <SEO
        title="Activate your customer portal · Tech Emergency"
        description="Set your password to activate your Tech Emergency customer portal."
        path="/portal/activate"
        noIndex
      />
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {state === "loading" && (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-zinc-500">
            <Loader2 className="animate-spin" />
            <span className="text-sm">Checking your activation link…</span>
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="portal-activate-invalid">
            <div className="te-overline mb-3">/ Activation link</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-3">
              That link can&apos;t be used.
            </h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">{tokenError}</p>
            <p className="text-zinc-500 text-xs leading-relaxed mb-6">
              No worries — give us a call on{" "}
              <a href="tel:1800836390" className="text-white hover:text-[var(--te-primary)] transition">
                1800 836 390
              </a>{" "}
              and we&apos;ll send you a fresh invite.
            </p>
            <button
              type="button"
              onClick={() => navigate("/portal/login")}
              className="te-btn te-btn-ghost"
            >
              Go to sign-in
            </button>
          </div>
        )}

        {state === "totp" && challenge && (
          <TotpStep
            challenge={challenge}
            issuer="Tech Emergency · Portal"
            email={emailHint}
            code={code}
            setCode={setCode}
            codeRef={codeRef}
            submit={submitTotp}
            submitting={submitting}
            restart={() => { setChallenge(null); setCode(""); setState("ready"); }}
            error={submitError}
            testidPrefix="portal-activate-2fa"
          />
        )}

        {state === "done" && (
          <div className="text-center py-12" data-testid="portal-activate-done">
            <div
              className="w-12 h-12 mx-auto mb-4 flex items-center justify-center"
              style={{ background: "var(--te-primary)" }}
            >
              <ShieldCheck size={22} color="#fff" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">You&apos;re in.</h1>
            <p className="text-zinc-400 text-sm">Taking you to your portal…</p>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="te-overline mb-3">/ Activate your portal</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-3">
              {nameHint ? `Welcome, ${nameHint.split(" ")[0]}.` : "Welcome."}
            </h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              Activating for <strong className="text-white">{emailHint}</strong>. Choose a
              password and you&apos;re in.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="portal-activate-form">
              <label className="block">
                <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
                  <Lock size={11} /> New password
                </span>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="portal-activate-password"
                    autoComplete="new-password"
                    className="w-full bg-black/40 border border-white/10 px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="portal-activate-toggle-pw"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-white transition"
                  >
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </label>

              <label className="block">
                <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
                  <Lock size={11} /> Confirm password
                </span>
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="portal-activate-confirm"
                  autoComplete="new-password"
                  className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
                />
              </label>

              <ul className="space-y-1.5 my-4" data-testid="portal-activate-rules">
                <Rule ok={rules.length} label="At least 10 characters" />
                <Rule ok={rules.digit} label="Contains at least one number" />
                <Rule ok={rules.symbol} label="Contains at least one symbol (!@#$ etc.)" />
                <Rule ok={rules.match} label="Both passwords match" />
              </ul>

              {submitError && (
                <div
                  className="text-sm px-3 py-2 border border-red-500/30 bg-red-500/10 text-red-300 flex items-start gap-2"
                  data-testid="portal-activate-error"
                >
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!allOk || submitting}
                data-testid="portal-activate-submit"
                className="te-btn te-btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Activating…
                  </>
                ) : (
                  <>Activate &amp; sign in</>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </PortalShell>
  );
}
